"use client";

import { useEffect, useRef, useState } from "react";
import { isFunction } from "@huub-web/utils";
import { TargetPositioner, DebugLabel, useMaybePubSub } from "@huub-web/ui";

import { visualDebugContainerStyle } from "@huub-web/ui/theme/css/visualDebugContainer.css";

import { TemplateRegistry } from "../../services";
import type { TemplateKey } from "../../types";
import { useMaybeTemplates } from "../../hooks/useTemplates";

export interface TemplateParserProps<T extends TemplateKey> {
  id: string;
  templateKey: T;
  level?: number;
  data: (typeof TemplateRegistry)[T]["$propsType"];
}

/**
 * TemplateParser component is responsible for parsing a template based on the templateKey.
 * It will look up the template in the TemplateRegistry and render the template.
 * If a wrap function is provided in the TemplateRegistry, it will wrap the template with the provided wrap function.
 */
export const TemplateParser = <T extends TemplateKey>({
  id,
  level = 0,
  templateKey,
  data,
}: TemplateParserProps<T>) => {
  const pubSub = useMaybePubSub();
  const templates = useMaybeTemplates();
  const ref = useRef<HTMLElement>(null);

  const templateEntry = TemplateRegistry[templateKey];
  const alreadyWarnedRef = useRef<boolean>(false);

  const [debugLabelActive, setDebugLabelActive] = useState(false);

  // No ID
  // ---------------------

  useEffect(() => {
    if (id || alreadyWarnedRef.current) {
      return;
    }

    console.warn(`[TemplateParser] Template has no ID (${templateKey})`, ref);

    if (templates?.debug) {
      pubSub?.publish("snackbar", {
        title: "[TemplateParser]",
        body: `Template '${templateKey}' has no ID`,
        variant: "warning",
        autoHide: false,
      });
    }

    alreadyWarnedRef.current = true;
  }, [templates?.debug, id, pubSub, templateKey]);

  // Debug
  // ---------------------

  useEffect(() => {
    if (!templates?.debug || !ref.current) {
      return;
    }

    if (typeof ref.current.addEventListener !== "function") {
      console.warn(
        `[TemplateParser] ref is not of correct type for template "${templateKey}"`,
        ref.current,
      );
      return;
    }

    ref.current.addEventListener("mouseenter", () => setDebugLabelActive(true));
    ref.current.addEventListener("mouseleave", () =>
      setDebugLabelActive(false),
    );

    return () => {
      ref.current?.removeEventListener("mouseenter", () =>
        setDebugLabelActive(true),
      );
      ref.current?.removeEventListener("mouseleave", () =>
        setDebugLabelActive(false),
      );
    };
  }, [templates?.debug, templateKey]);

  // Template is not found
  // ---------------------

  const alreadyErroredRef = useRef<boolean>(false);

  useEffect(() => {
    if (
      (!templateEntry || !templateEntry.template) &&
      !alreadyErroredRef.current
    ) {
      alreadyErroredRef.current = true;
      console.error(
        `[TemplateParser] Template with templateKey "${templateKey}" not found (${id})`,
      );
      return;
    }
  }, [templateKey, id, templateEntry]);

  // Return null if template is not found
  if (!templateEntry || !templateEntry.template) {
    return null;
  }

  return (
    <>
      {isFunction(templateEntry.wrap) ? (
        templateEntry.wrap(
          level,
          <templateEntry.template ref={ref} id={id} {...data} />,
          data,
        )
      ) : (
        <templateEntry.template ref={ref} id={id} {...data} />
      )}
      {templates?.debug && (
        <TargetPositioner
          targetRef={ref}
          content={
            <div
              className={visualDebugContainerStyle}
              style={{ width: "100%", height: "100%" }}
            >
              <DebugLabel>
                [{templateKey}] {id}
              </DebugLabel>
            </div>
          }
          zIndex={-1}
          active={debugLabelActive}
          mode="fixed"
          placement="center"
          applyDimensions
        />
      )}
    </>
  );
};
